import { useSyncExternalStore } from 'react';

/** Light / dark theme, persisted to localStorage and reflected on <html data-theme>. */
export type Theme = 'light' | 'dark';

const KEY = 'wand-theme';

function read(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'dark' || v === 'light') return v;
  } catch {
    /* ignore */
  }
  return 'dark';
}

let current: Theme = read();
const listeners = new Set<() => void>();

/** Apply the data-theme attribute to <html> (matches the index.html bootstrap). */
export function initTheme(): void {
  document.documentElement.setAttribute('data-theme', current);
}

export function applyTheme(theme: Theme): void {
  current = theme;
  try {
    localStorage.setItem(KEY, theme);
    // The governance demo shares this origin and stores the same preference under its own key.
    // Writing both, plus a mark saying who wrote last, is what makes one switch mean one theme
    // across the two apps rather than two switches that disagree.
    localStorage.setItem('wand-theme-v2', theme);
  } catch {
    /* ignore */
  }
  document.documentElement.setAttribute('data-theme', theme);
  listeners.forEach((l) => l());
}

export function toggleTheme(): void {
  applyTheme(current === 'dark' ? 'light' : 'dark');
}

/** React hook: re-renders when the theme changes. */
export function useTheme(): Theme {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => current,
    () => current,
  );
}
